import React from 'react';
import PropTypes from 'prop-types';
import './index.css';
import FlipMove from 'react-flip-move';
import UploadIcon from './UploadIcon.svg';
import ImageZoom from 'react-medium-image-zoom'
import FileWrapper from "./FileWrapper";
import {injectIntl} from 'react-intl';
import i18n from "../../main/i18n";

const styles = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexWrap: "wrap",
  width: "100%"
};

class ReactImageUploadComponent extends React.Component {
  constructor(props) {
    super(props);

    this.msg = i18n.getMsg(props.intl);

    this.state = {
      files: [], // blobs
      filesProgress: {}, // helper to detect which files are in progress
      notAcceptedFileType: [],
      notAcceptedFileSize: []
    };
    this.inputElement = '';
    this.onDropFile = this.onDropFile.bind(this);
    this.onUploadClick = this.onUploadClick.bind(this);
    this.triggerFileUpload = this.triggerFileUpload.bind(this);
  }

  /*
     On button click, trigger input file to open
     */
  triggerFileUpload() {
    this.inputElement.click();
  }

  onUploadClick(e) {
    // Fixes https://github.com/JakeHartnell/react-images-upload/issues/55
    e.target.value = null;
  }

  /*
     Handle file validation
     */
  onDropFile(e) {
    const files = e.target.files;
    const allFilePromises = [];

    // Iterate over all uploaded files
    for (let i = 0; i < files.length; i++) {
      let f = files[i];
      // Check for file extension
      if (!this.hasExtension(f.name)) {
        const newArray = this.state.notAcceptedFileType.slice();
        newArray.push(f.name);
        this.setState({notAcceptedFileType: newArray});
        continue;
      }
      // Check for file size
      if (f.size > this.props.maxFileSize) {
        const newArray = this.state.notAcceptedFileSize.slice();
        newArray.push(f.name);
        this.setState({notAcceptedFileSize: newArray});
        continue;
      }

      allFilePromises.push(FileWrapper.fromBlob(f));
    }

    Promise.all(allFilePromises)
      .then(newFilesWrappers => {
        const files = [...this.state.files, ...newFilesWrappers];
        this.setState({files}, () => {
          this.props.onChange(this.state.files);
        });
      });
  }

  /*
   Render the upload icon
   */
  renderIcon() {
    if (this.props.withIcon) {
      return <img src={UploadIcon} className="uploadIcon" alt={this.msg('imgAlt.upload')}/>;
    }
  }

  /*
     Render label
     */
  renderLabel() {
    if (this.props.withLabel) {
      return <p className={this.props.labelClass} style={this.props.labelStyles}>{this.props.label}</p>
    }
  }

  /*
	 Check file extension (onDropFile)
	 */
  hasExtension(fileName) {
    const pattern = '(' + this.props.imgExtension.join('|').replace(/\./g, '\\.') + ')$';
    return new RegExp(pattern, 'i').test(fileName);
  }

  /*
     Remove the image from state
     */
  removeImage(idx) {
    const filteredFiles = this.state.files.filter((e, index) => index !== idx);
    this.setState({files: filteredFiles}, () => {
      this.props.onChange(this.state.files);
    });
  }

  renderImageSpinnerIfAny(idx) {
    if (this.state.filesProgress[idx] === true) {
      return <span className={"fileSpinner"}/>
    }
  }

  rotateImage(idx) {

    let filesProgress = this.state.filesProgress;
    filesProgress[idx] = true;
    this.setState({filesProgress});

    const files = [...this.state.files];
    const fw = files[idx];

    FileWrapper.rotate(fw)
      .then((updatedWrapper) => {
        files[idx] = updatedWrapper;
        this.setState({files}, () => {
          this.props.onChange(this.state.files);
        });
      })
      .finally(() => {
        delete filesProgress[idx];
        this.setState({filesProgress});
      })
  }

  /*
     Check if any errors && render
     */
  renderErrors() {
    let notAccepted = '';
    if (this.state.notAcceptedFileType.length) {
      notAccepted = this.state.notAcceptedFileType.map((error, index) => {
        return (
          <div className={'errorMessage ' + this.props.errorClass} key={index} style={this.props.errorStyle}>
            * {error} {this.props.fileTypeError}
          </div>
        )
      });
    }
    if (this.state.notAcceptedFileSize.length) {
      notAccepted = this.state.notAcceptedFileSize.map((error, index) => {
        return (
          <div className={'errorMessage ' + this.props.errorClass} key={index} style={this.props.errorStyle}>
            * {error} {this.props.fileSizeError}
          </div>
        )
      });
    }
    return notAccepted;
  }

  /*
     Render preview images
     */
  renderPreview() {
    return (
      <div className="uploadPicturesWrapper">
        <FlipMove enterAnimation="fade" leaveAnimation="fade" style={styles}>
          {this.renderPreviewPictures()}
        </FlipMove>
      </div>
    );
  }

  renderPreviewPictures() {
    return this.state.files.map((fileWrapper, index) => {
      return (
        <div key={index} className="uploadPictureContainer">
          <button type={"button"} disabled={this.state.filesProgress[index]} className="action imageButton"
                  onClick={() => this.removeImage(index)}>&times;</button>

          {/*&#x21BB; is the hexadecimal value of the "clockwise open circle arrow" unicode character*/}
          <button type={"button"} disabled={this.state.filesProgress[index]} className="action imageButton" id="rotate"
                  onClick={() => this.rotateImage(index)}>&#x21BB;</button>
          <ImageZoom
            image={{
              src: fileWrapper.thumbnailUrl,
              alt: this.msg('imgAlt.preview'),
              className: 'uploadPicture'
            }}
            zoomImage={{
              src: fileWrapper.dataURL,
              alt: fileWrapper.fileName,
              className: 'uploadPicture'
            }}
            shouldReplaceImage={false}/>
          {this.renderImageSpinnerIfAny(index)}
        </div>
      );
    });
  }

  render() {
    return (
      <div className={"fileUploader " + this.props.className} style={this.props.style}>
        <div className="fileContainer">
          {this.renderIcon()}
          {this.renderLabel()}
          <div className="errorsContainer">
            {this.renderErrors()}
          </div>
          <button
            type={this.props.buttonType}
            className={"chooseFileButton " + this.props.buttonClassName}
            style={this.props.buttonStyles}
            onClick={this.triggerFileUpload}>
            {this.props.buttonText}
          </button>
          <input
            type="file"
            ref={input => this.inputElement = input}
            name={this.props.name}
            multiple={!this.props.singleImage}
            onChange={this.onDropFile}
            onClick={this.onUploadClick}
            accept={this.props.accept}/>
          {this.props.withPreview ? this.renderPreview() : null}
        </div>
      </div>
    )
  }
}

ReactImageUploadComponent.defaultProps = {
  className: '',
  buttonClassName: "",
  buttonStyles: {},
  withPreview: false,
  accept: "image/*",
  name: "",
  withIcon: true,
  buttonType: "submit",
  withLabel: true,
  labelStyles: {},
  labelClass: "",
  imgExtension: ['.jpg', '.gif', '.png'],
  maxFileSize: 5242880,
  errorClass: "",
  style: {},
  errorStyle: {},
  singleImage: false,
  onChange: () => {
  }
};

ReactImageUploadComponent.propTypes = {
  style: PropTypes.object,
  className: PropTypes.string,
  onChange: PropTypes.func,
  onDelete: PropTypes.func,
  buttonClassName: PropTypes.string,
  buttonStyles: PropTypes.object,
  buttonType: PropTypes.string,
  withPreview: PropTypes.bool,
  accept: PropTypes.string,
  name: PropTypes.string,
  withIcon: PropTypes.bool,
  buttonText: PropTypes.string,
  withLabel: PropTypes.bool,
  label: PropTypes.string,
  labelStyles: PropTypes.object,
  labelClass: PropTypes.string,
  imgExtension: PropTypes.array,
  maxFileSize: PropTypes.number,
  fileSizeError: PropTypes.string,
  fileTypeError: PropTypes.string,
  errorClass: PropTypes.string,
  errorStyle: PropTypes.object,
  singleImage: PropTypes.bool
};

export default injectIntl(ReactImageUploadComponent);
